import { UpdateHeroBlockModel, UpdateHeroTalentBlockModel, UpdateHeroUpgradesBlockModel, UpdateTalentType } from "../../../../api/repositories/stats/models/updates"
import { CdnUrl, UpdateHeroTalent } from "../../../../common/constants"
import { getByLocalizationRows, localize } from "../../../../common/services/localization"
import { combineAddress } from "../../../../common/utils"
import { HeroAttributeImage, HeroImage, TalentImage, UpgradeImage } from "../../ui/file-contents"
import { LocalizeInnerHtml, LocalizeListElement, LocalizeUpdateChange } from "../../ui/other"

export const UpdateHeroesBlock: React.FC<{
    heroes: UpdateHeroBlockModel[]
}> = ({ heroes }) => {
    return <div>
        {heroes.map((k, i) => <UpdateHeroBlock
            key={i}
            hero={k}
            isLast={i == heroes.length - 1}
        />)}
    </div>
}

const UpdateHeroBlock: React.FC<{
    hero: UpdateHeroBlockModel
    isLast: boolean
}> = ({ hero, isLast }) => {
    return <div id={hero.urlName} className={`flex flex-col gap-[12px] py-[22px] ${!isLast && 'border-b-dark'}`}>
        <div className="flex flex-row gap-[12px] hover:pointer items-center">
            <a href={`/heroes/${hero.urlName}`}>
                <HeroImage
                    className="h-[60px] lg:h-full"
                    heroName={hero.name}
                    size="178x100"
                />
            </a>
            <HeroAttributeImage
                className="hidden lg:block h-[33px] w-[33px]"
                attr={hero.attribute}
            />
            <span className="font-['Reaver'] text-[22px] lg:text-[32px] font-semibold uppercase">
                {hero.userFrendlyName}
            </span>
        </div>
        {
            hero.ruRows && <LocalizeInnerHtml className="py-[12px]" ruRows={hero.ruRows} engRows={hero.engRows} />
        }
        {
            hero.upgrades.length > 0 && <div className="pb-[12px]">
                {hero.upgrades.map((k, i) => <UpgradeBlock
                    key={i}
                    upgrage={k}
                    isLast={i == hero.upgrades.length - 1}
                />)}
            </div>
        }
        {
            hero.talents.length > 0 && <TalentsBlock talents={hero.talents} />
        }
    </div>
}

const TalentsBlock: React.FC<{
    talents: UpdateHeroTalentBlockModel[]
}> = ({ talents }) => {

    const heroTalent = talents.find(f => f.name == UpdateHeroTalent)
    const talentsWithOutHeroTalents = talents.filter(f => f.name != UpdateHeroTalent)

    return <div className={`flex flex-col gap-[18px]'}`}>
        {
            heroTalent && <HeroTalentsBlock talent={heroTalent} />
        }
        {
            talentsWithOutHeroTalents.length > 0 && <div className="flex flex-col">
                {talentsWithOutHeroTalents.map((k, i) => <TalentBlock
                    key={i}
                    talent={k}
                    isLast={i == talents.length - 1} />)
                }
            </div>
        }
    </div>
}

const TalentBlock: React.FC<{
    talent: UpdateHeroTalentBlockModel
    isLast: boolean
}> = ({ talent, isLast }) => {
    return <div className={`flex flex-col gap-[18px] py-[18px]`}>
        <div className="flex flex-row gap-[16px] items-center text-[20px]">
            <TalentImage
                className="h-[50px] lg:h-full"
                name={talent.name}
                size='72x72'
            />
            <LocalizeUpdateChange changeType={talent.changeType} />
        </div>
        {
            talent.abilityRuRows && <LocalizeInnerHtml
                ruRows={talent.abilityRuRows}
                engRows={talent.abilityEngRows}
            />
        }
        {
            talent.orangeRuRows && <TalentRowsBlock
                ruRows={talent.orangeRuRows}
                engRows={talent.orangeEngRows}
                type={'Orange'}
            />
        }
        {
            talent.purpleRuRows && <TalentRowsBlock
                ruRows={talent.purpleRuRows}
                engRows={talent.purpleEngRows}
                type={'Purple'}
            />
        }
        {
            talent.blueRuRows && <TalentRowsBlock
                ruRows={talent.blueRuRows}
                engRows={talent.blueEngRows}
                type={'Blue'}
            />
        }
    </div>
}

const HeroTalentsBlock: React.FC<{
    talent: UpdateHeroTalentBlockModel
}> = ({ talent }) => {
    return <div className="flex flex-col gap-[12px] py-[18px]">
        <span className="uppercase text-[18px] font-semibold font-['Reaver']">
            {localize('update_hero_talent_title')}
        </span>
        {
            talent.purpleRuRows && <TalentRowsBlock
                ruRows={talent.purpleRuRows}
                engRows={talent.purpleEngRows}
                type="Purple"
            />
        }
        {
            talent.blueRuRows && <TalentRowsBlock
                ruRows={talent.blueRuRows}
                engRows={talent.blueEngRows}
                type="Blue"
            />
        }
    </div>
}

const UpgradeBlock: React.FC<{
    upgrage: UpdateHeroUpgradesBlockModel
    isLast: boolean
}> = ({ upgrage, isLast }) => {
    return <div className={`flex flex-col gap-[12px] py-[12px] ${isLast && 'border-b-dark'}`}>
        <div className="flex flex-row gap-[16px] items-center text-[20px]">
            <UpgradeImage className="lg:w-[72px]" type={upgrage.type} />
            <LocalizeUpdateChange changeType={upgrage.changeType} />
        </div>
        <LocalizeInnerHtml
            ruRows={upgrage.ruRows}
            engRows={upgrage.engRows}
        />
    </div>
}

const TalentRowsBlock: React.FC<{
    ruRows?: string
    engRows?: string
    type: UpdateTalentType
}> = ({ ruRows, engRows, type }) => {

    return <div className={`flex flex-col gap-[18px] p-[12px] updates-block-${type}`}>
        <div className={`px-[8px] py-[4px] tracking-wide shadow-dark-xs text-[14px] w-fit uppercase updates-block-${type}-1`}>
            {localize(`talent-block-title-${type}`)}
        </div>
        <LocalizeInnerHtml
            ruRows={ruRows}
            engRows={engRows}
        />
    </div>
}